import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaBars,
  FaChevronDown,
  FaHome,
  FaSignInAlt,
  FaTimes,
} from "react-icons/fa";

import { CLUB_SECTIONS, FALLBACK_LOGO } from "./navbarData";

export default function NavbarMainBar({
  settings,
  logoSrc,
  pagesActives,
  isAuthenticated,
  IconComponent,
  mobileOpen,
  onToggleMobile,
  onScrollToSection,
  onInscription,
  onApplication,
}) {
  const [clubOpen, setClubOpen] = useState(false);
  const clubMenuRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (clubMenuRef.current && !clubMenuRef.current.contains(event.target)) {
        setClubOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleClubSectionClick = (targetId) => {
    setClubOpen(false);
    onScrollToSection(targetId);
  };

  return (
    <div className="h-[84px] bg-white/90 dark:bg-slate-950/90">
      <div className="mx-auto flex h-full max-w-7xl items-center justify-between gap-4 px-4 lg:px-8">
        <Link
          to="/"
          onClick={() => onScrollToSection("accueil")}
          className="flex min-w-0 items-center gap-3"
        >
          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden sm:h-20 sm:w-20">
            <img
              src={logoSrc}
              alt={`Logo ${settings.nom_club || "du club"}`}
              className="h-full w-full object-contain"
              loading="lazy"
              onError={(event) => {
                event.currentTarget.src = FALLBACK_LOGO;
              }}
            />
          </div>

          <div className="min-w-0 leading-tight">
            <p
              title={settings.nom_club || "MonAppliClub"}
              className="
    max-w-[190px]
    overflow-hidden
    break-words
    font-poppins
    text-[1.1rem]
    font-bold
    leading-tight
    text-slate-900
    dark:text-white
    sm:max-w-[320px]
    sm:text-[1.45rem]
    lg:max-w-[300px]
    xl:max-w-[360px]
    [display:-webkit-box]
    [-webkit-box-orient:vertical]
    [-webkit-line-clamp:2]
  "
            >
              {settings.nom_club || "MonAppliClub"}
            </p>
          </div>
        </Link>

        <nav className="hidden items-center gap-3 lg:flex xl:gap-5">
          <button
            type="button"
            onClick={() => onScrollToSection("accueil")}
            className="flex h-10 w-10 items-center justify-center rounded-full text-slate-700 transition hover:bg-slate-100 hover:text-primary dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-orange-400"
            aria-label="Accueil"
            title="Accueil"
          >
            <FaHome />
          </button>

          <div className="relative" ref={clubMenuRef}>
            <button
              type="button"
              onClick={() => setClubOpen((previous) => !previous)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition ${
                clubOpen
                  ? "bg-slate-950 text-white shadow-sm dark:bg-orange-500"
                  : "text-slate-700 hover:bg-slate-100 hover:text-primary dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-orange-400"
              }`}
            >
              Club
              <FaChevronDown
                className={`text-xs transition ${clubOpen ? "rotate-180" : ""}`}
              />
            </button>

            {clubOpen && (
              <div className="absolute left-1/2 top-[calc(100%+14px)] z-50 w-[760px] -translate-x-1/2 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
                <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-700 dark:bg-slate-950">
                  <p className="text-sm font-bold text-slate-900 dark:text-white">
                    Le Club
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                    Accédez rapidement aux rubriques principales de la page
                    d’accueil.
                  </p>
                </div>

                <div className="max-h-[calc(100vh-170px)] overflow-y-auto p-4">
                  <div className="grid grid-cols-2 gap-2 xl:grid-cols-3">
                    {CLUB_SECTIONS.map((item) => {
                      const Icon = item.icon;

                      return (
                        <button
                          key={item.targetId}
                          type="button"
                          onClick={() => handleClubSectionClick(item.targetId)}
                          className="flex items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm font-bold text-slate-700 transition hover:bg-slate-100 hover:text-primary dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-orange-400"
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-orange-50 text-orange-600 dark:bg-orange-950/40 dark:text-orange-300">
                            <Icon />
                          </span>
                          <span className="line-clamp-1">{item.label}</span>
                        </button>
                      );
                    })}
                  </div>

                  {pagesActives.length > 0 && (
                    <div className="mt-4 border-t border-slate-200 pt-4 dark:border-slate-700">
                      <p className="mb-3 px-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
                        Pages personnalisées
                      </p>

                      <div className="grid grid-cols-2 gap-2 xl:grid-cols-3">
                        {pagesActives.map((page) => (
                          <Link
                            key={page.id}
                            to={`/page/${page.slug}`}
                            onClick={() => setClubOpen(false)}
                            className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-100 hover:text-primary dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-orange-400"
                          >
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                              {String(page.titre || "P").charAt(0)}
                            </span>
                            <span className="line-clamp-1">{page.titre}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={onInscription}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-100 hover:text-primary dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-orange-400"
          >
            <FaSignInAlt />
            Inscription
          </button>

          {isAuthenticated && (
            <button
              type="button"
              onClick={onApplication}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-glow transition hover:-translate-y-0.5 hover:bg-slate-950 dark:hover:bg-orange-500"
            >
              <IconComponent className="text-lg" />
              Application
            </button>
          )}
        </nav>

        <button
          type="button"
          onClick={onToggleMobile}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-800 shadow-sm transition hover:shadow-md dark:border-slate-700 dark:bg-slate-900 dark:text-white lg:hidden"
          aria-label="Ouvrir le menu"
        >
          {mobileOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>
    </div>
  );
}
